import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { LocalStorageKey } from 'src/app/constants/local-storage.keys';

@Injectable({ providedIn: 'root' })
export class TextSizeService {
    private document = inject(DOCUMENT);

    private _percentage = signal(100);

    public readonly minimumPercentage: number = 80;
    public readonly maximumPercentage: number = 120;
    public readonly percentageStep: number = 5;
    public readonly defaultPercentage: number = 100;

    public readonly percentage: Signal<number> = this._percentage.asReadonly();

    public initialize(): void {
        let percentage = this.defaultPercentage;

        try {
            const storedPercentage = localStorage.getItem(LocalStorageKey.textSize);
            if (storedPercentage) percentage = Number(storedPercentage);
        } catch {}

        this.applyPercentage(this.normalizePercentage(percentage));
    }

    public setPercentage(percentage: number): void {
        const normalizedPercentage = this.normalizePercentage(percentage);
        this.applyPercentage(normalizedPercentage);

        try {
            localStorage.setItem(LocalStorageKey.textSize, String(normalizedPercentage));
        } catch {}
    }

    public reset(): void {
        this.setPercentage(this.defaultPercentage);
    }

    private normalizePercentage(percentage: number): number {
        if (!Number.isFinite(percentage)) return this.defaultPercentage;

        const steppedPercentage = Math.round(percentage / this.percentageStep) * this.percentageStep;
        return Math.min(this.maximumPercentage, Math.max(this.minimumPercentage, steppedPercentage));
    }

    private applyPercentage(percentage: number): void {
        this._percentage.set(percentage);
        this.document.documentElement.setAttribute('data-text-size', String(percentage));
    }
}
